/**
 * THESIS: A Central da Santa Casa organiza o turno por fluxo auditável e recusa o dashboard genérico de cards.
 * OWN-WORLD: papel clínico claro, azul-plantão, ciano de ação, linhas discretas e dados tabulares.
 * STORY: cada perfil vê o que acontece agora, resolve pendências e chega ao registro de origem.
 * FIRST VIEWPORT: linha do turno domina o início; fila crítica e risco operacional ficam no mesmo campo.
 * FORM: instrumento operacional denso, navegação persistente e painéis laterais que preservam contexto.
 */
import { lazy, Suspense, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Route, Routes } from 'react-router-dom'
import { api } from './api'
import type { Usuario } from './types'
import Login from './Login'
import Shell from './Shell'
import { LoadingState } from './components'

const HomeScreen = lazy(() => import('./screens/HomeScreen'))
const PacientesScreen = lazy(() => import('./screens/PacientesScreen'))
const AgendaScreen = lazy(() => import('./screens/AgendaScreen'))
const SessoesScreen = lazy(() => import('./screens/SessoesScreen'))
const EstoqueScreen = lazy(() => import('./screens/EstoqueScreen'))
const FaturamentoScreen = lazy(() => import('./screens/FaturamentoScreen'))
const ComunicacaoScreen = lazy(() => import('./screens/ComunicacaoScreen'))
const IndicadoresScreen = lazy(() => import('./screens/IndicadoresScreen'))
const AuditoriaScreen = lazy(() => import('./screens/AuditoriaScreen'))
const AdministracaoScreen = lazy(() => import('./screens/AdministracaoScreen'))

export default function App() {
  const queryClient = useQueryClient()
  const [sessionUser, setSessionUser] = useState<Usuario | null>(null)
  const profile = useQuery({
    queryKey: ['perfil'],
    queryFn: () => api<{ usuario: Usuario }>('/autenticacao/perfil'),
    retry: false,
  })
  const user = sessionUser || profile.data?.usuario

  async function logout() {
    await api('/autenticacao/sair', { method: 'POST' }).catch(() => undefined)
    setSessionUser(null)
    queryClient.clear()
    profile.refetch()
  }

  if (profile.isLoading && !sessionUser) return <div className="boot-screen"><img src="/automyx-wordmark-oficial.png" alt="Automyx" /><LoadingState label="Preparando a unidade" /></div>
  if (!user) return <Login onSuccess={(authenticatedUser) => { setSessionUser(authenticatedUser); queryClient.setQueryData(['perfil'], { usuario: authenticatedUser }) }} />

  return <Shell user={user} onLogout={logout}>
    <Suspense fallback={<LoadingState label="Abrindo a área de trabalho" />}>
    <Routes>
      <Route path="/" element={<HomeScreen user={user} />} />
      <Route path="/pacientes" element={<PacientesScreen />} />
      <Route path="/agenda" element={<AgendaScreen />} />
      <Route path="/sessoes" element={<SessoesScreen />} />
      <Route path="/estoque" element={<EstoqueScreen />} />
      <Route path="/faturamento" element={<FaturamentoScreen />} />
      <Route path="/comunicacao" element={<ComunicacaoScreen />} />
      <Route path="/indicadores" element={<IndicadoresScreen />} />
      <Route path="/auditoria" element={user.permissoes.includes('auditoria.visualizar') ? <AuditoriaScreen /> : <Navigate to="/" replace />} />
      <Route path="/administracao" element={user.permissoes.includes('administracao.usuarios') ? <AdministracaoScreen /> : <Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  </Shell>
}
